/**
 * @fileoverview Shared label + status-pill wrapper for every widget.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render the canonical label / asterisk / help-text / N/A button /
 *   PASS-FAIL pill / hidden-banner / disabled-state / error-message
 *   chrome consistently across all 18 widgets.
 * - Surface the data-attributes the renderer's E2E + visual tests rely on
 *   (`data-field-id`, `data-widget-type`).
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` for `ResolvedFieldStateV2` (Phase 2).
 *
 * Architecture:
 * - Presentation-only. No state, no effects, no host coupling.
 * - Widgets compose: `<FieldWrapper {...wrap}>...control...</FieldWrapper>`.
 *
 * Notes:
 * - All a11y attributes from D-0.17 baseline live here so a single fix
 *   propagates everywhere.
 * - N/A is rendered as an inline button to the RIGHT of the widget control
 *   (not below it) so inspectors can flip it with a single thumb tap
 *   without breaking the visual line of the question. The button uses
 *   `pointer-events-auto` to remain interactive even when the wrapper
 *   greys out the rest of the field via `pointer-events-none` (because
 *   the inspector needs to tap N/A again to clear it).
 *
 * @component FieldWrapper
 *
 * Purpose: shared visual chrome around an interactive widget control.
 *
 * Props:
 * - fieldId: per-field DOM id + data-attribute anchor.
 * - widgetType: canonical FieldType marker used by E2E selectors.
 * - label: human-readable label.
 * - required: static or rule-driven required state (renders asterisk).
 * - helpText: optional descriptive subtext beneath the label.
 * - resolved: ResolvedFieldStateV2 from engine v2 (hidden / disabled / required).
 * - isNA: when true, dims the control + counts the field as answered.
 * - naEnabled: when true, surfaces the inline N/A button to the right.
 * - onToggleNA: optional handler for the N/A button click.
 * - passFailStatus: 'pass' | 'fail' | 'neutral' — drives the PASS/FAIL pill.
 * - errorMessage: optional validation error rendered under the control.
 * - children: the actual interactive control (the widget body).
 *
 * Behavior:
 * - Renders nothing when `resolved.hidden === true` (conduct + preview both).
 * - PASS / FAIL pills and the asterisk are presentational only; no state
 *   mutation happens here.
 *
 * Data Source:
 * - local (pure presentation).
 *
 * Constraints:
 * - No API calls, no Dexie writes, no host imports. Tests with raw render
 *   should not need any provider wrap.
 *
 * Performance:
 * - Re-renders only when own props change.
 *
 * @contract FieldWrapperProps
 * Purpose: input contract for the per-widget visual chrome.
 *
 * @contract PassFailStatus
 * Purpose: 'pass' | 'fail' | 'neutral' — drives the colored status pill.
 */
import { type ReactNode, useId, useState } from 'react';
import type { ResolvedFieldStateV2 } from '@firesquirrel/core-domain';

export type PassFailStatus = 'pass' | 'fail' | 'neutral';

export interface FieldWrapperProps {
  fieldId: string;
  widgetType: string;
  label?: string;
  helpText?: string;
  required?: boolean;
  resolved?: ResolvedFieldStateV2;
  isNA?: boolean;
  naEnabled?: boolean;
  onToggleNA?: (next: boolean) => void;
  passFailStatus?: PassFailStatus;
  errorMessage?: string;
  /**
   * True when the field is required and has no value. Surface a red ring
   * + "Required" helper text once the user has touched (focused-and-blurred)
   * the control. Computed by `<FieldRenderer>` from a single source of truth
   * (`validation/fieldIssues.ts`) so the badge / panel / wrapper never disagree.
   */
  requiredUnmet?: boolean;
  children: ReactNode;
}

export function FieldWrapper(props: FieldWrapperProps) {
  const {
    fieldId, widgetType, label, helpText,
    required, resolved, isNA, naEnabled,
    onToggleNA, passFailStatus, errorMessage,
    requiredUnmet, children,
  } = props;

  const helpId = useId();
  const errorId = useId();
  const requiredHelpId = useId();

  // Touched state — flips on the first focus-leave inside the wrapper. Lets
  // us avoid screaming "Required" at the user before they've even tried to
  // engage with the control.
  const [touched, setTouched] = useState(false);

  // B16-aware visibility: the renderer must respect resolved.hidden coming
  // from engine v2. Returning null keeps the field out of the DOM entirely
  // for the conduct flow; preview surfaces apply a separate "hidden with data"
  // banner upstream (D-0.6 §"Per-widget rows").
  if (resolved?.hidden) return null;

  const requiredEffective = !!(required || resolved?.required);
  const disabled = !!resolved?.disabled;
  // Show the unmet ring once the user has touched the field. We always
  // surface the data attribute (so the summary panel + tab badges can pick
  // it up before the user has touched anything), but the visible ring waits
  // for `touched` to avoid badgering on first paint.
  const showRequiredRing = !!requiredUnmet && touched && !isNA && !disabled;
  const ringClass = showRequiredRing
    ? 'ring-1 ring-red-300 rounded-sm p-1 -m-1'
    : '';

  const showNAButton = !!naEnabled && !!onToggleNA;

  return (
    <div
      data-field-id={fieldId}
      data-widget-type={widgetType}
      data-required={requiredEffective ? 'true' : 'false'}
      data-is-na={isNA ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
      data-pass-fail={passFailStatus ?? 'neutral'}
      data-required-unmet={requiredUnmet ? 'true' : 'false'}
      data-touched={touched ? 'true' : 'false'}
      onBlur={(e) => {
        // Only flip touched when focus actually leaves the wrapper (not when
        // it just moves between sub-controls of a composite widget).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setTouched(true);
        }
      }}
      className={`flex flex-col gap-1 ${isNA ? 'opacity-60 pointer-events-none' : ''} ${ringClass}`.trim()}
    >
      {label && (
        <label
          htmlFor={fieldId}
          className="text-sm font-medium text-gray-800 flex items-center gap-1"
        >
          <span>{label}</span>
          {requiredEffective && <span aria-label="required" className="text-red-500">*</span>}
          {passFailStatus === 'pass' && (
            <span role="status" aria-label="Status: pass" data-pill="pass" className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              PASS
            </span>
          )}
          {passFailStatus === 'fail' && (
            <span role="status" aria-label="Status: fail" data-pill="fail" className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
              FAIL
            </span>
          )}
        </label>
      )}

      {/* Widget body sits in a flex row with the inline N/A button on the
          right. The widget container takes `flex-1 min-w-0` so it shrinks
          cleanly when long labels/options would otherwise push the N/A
          button off-screen. */}
      <div className="flex items-start gap-2">
        <div
          className="flex-1 min-w-0"
          aria-describedby={helpText ? helpId : undefined}
        >
          {children}
        </div>
        {showNAButton && (
          <button
            type="button"
            aria-pressed={!!isNA}
            aria-label={isNA ? 'Clear N/A' : 'Mark as N/A'}
            data-pill="na"
            data-na-active={isNA ? 'true' : 'false'}
            onClick={() => onToggleNA!(!isNA)}
            className={[
              // Inline + always interactive even when the wrapper greys out
              // the rest of the field.
              'shrink-0 pointer-events-auto self-start',
              // Sized for a comfortable thumb-tap on mobile (~36px tall,
              // ~44px wide). The user is allowed to flip N/A from any state.
              'inline-flex items-center justify-center min-w-[44px] h-9 px-2.5',
              'text-xs font-semibold rounded-sm border transition-colors',
              isNA
                ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
            ].join(' ')}
          >
            N/A
          </button>
        )}
      </div>

      {helpText && (
        <p id={helpId} className="text-xs text-gray-500">{helpText}</p>
      )}
      {errorMessage && (
        <p id={errorId} role="alert" className="text-xs text-red-600">{errorMessage}</p>
      )}
      {showRequiredRing && (
        <p
          id={requiredHelpId}
          role="alert"
          data-required-helper="true"
          className="text-xs text-red-600"
        >
          Required
        </p>
      )}
    </div>
  );
}
